import SideTool from "@/global/components/side-tool";
import { User } from "@/global/interface/project-manage/user";
import { Modal, Table, Tooltip } from "antd";
import classnames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import LinkProjectUserModal from "./link-modal";
import TnMessage from "@/global/components/message";
import { findProjectUsers, linkProjectUsers, unlinkProjectUsers } from "@/api/project-manage/project-user";
import { ContextMenuFC } from "tncet-common";
import { ReactComponent as InfoSvg } from './icons/info.svg';
import { ReactComponent as EditSvg } from './icons/edit.svg';
import { ReactComponent as DeleteSvg } from './icons/delete.svg';
import { ReactComponent as CheckSvg } from '@/global/icons/table/check.svg';
import { ReactComponent as CheckedSvg } from '@/global/icons/table/checked.svg';
import ProjectUserDetailModal from "./detail-modal";
import TnModal from "@/global/components/base-modal";
import EditProjectUserModal from "./edit-modal";

export default function UserManagerPanel() {

    const { projectId } = useParams();
    const [loading, setLoading] = useState<boolean>(false); // 表格loading
    const [multi, setMulti] = useState<boolean>(false); // 是否多选状态
    const [selectedRows, setSelectedRows] = useState<User[]>([]); // 多选选中的
    const [activeRow, setActiveRow] = useState<User>(null); // 选中的行
    const [users, setUsers] = useState<User[]>([]); // 项目用户

    const [addLoading, setAddLoading] = useState<boolean>(false); // 添加loading
    const [addVisible, setAddVisible] = useState<boolean>(false); // 添加弹窗

    const [detailVisible, setDetailVisible] = useState<boolean>(false); // 详情弹窗

    const [editVisible, setEditVisible] = useState<boolean>(false); // 编辑弹窗

    const [cmOpen, setCmOpen] = useState<boolean>(false); // 是否展开右键菜单
    const [top, setTop] = useState<number>(); // 右键菜单top
    const [left, setLeft] = useState<number>(); // 右键菜单left

    // 初始化
    useEffect(() => {
        if (!projectId) return;
        generateData();
    }, [projectId])

    // 获取项目用户
    const generateData = () => {
        setLoading(true);
        findProjectUsers(+projectId).then(res => {
            if (res.status === 200) {
                let data: User[] = res.data || [];
                setUsers(data);
            }
        }).catch(err => {
            TnMessage.error("获取项目用户失败");
        }).finally(() => {
            setLoading(false);
        })
    }

    // 按下工具条添加
    const onAddClick = () => {
        setAddVisible(true);
    }

    // 按下工具条编辑
    const onEditClick = (user: User) => {
        if (!user) {
            TnMessage.warning("未选中用户");
            return;
        }
        // TODO 编辑啥？
        setCmOpen(false);
        setEditVisible(true);
    }

    // 按下工具条删除
    const onDeleteClick = (multi: boolean, user: User, userList: User[]) => {
        setCmOpen(false);
        if ((multi && userList?.length === 0) || (!multi && !user)) {
            TnMessage.warning("未选中用户");
            return;
        }
        TnModal().deleteModal({
            onOk() {
                let userIds = [];
                if (multi) {
                    userIds = userList?.map(i => i.id);
                } else {
                    userIds.push(user?.id);
                }
                setLoading(true);
                Modal.destroyAll();
                unlinkProjectUsers(+projectId, userIds).then(res => {
                    if (res.status === 200) {
                        let newUsers = users?.filter(i => !userIds.some(u => u === i.id)) || [];
                        setUsers(newUsers);
                        TnMessage.success("删除成功");
                        if (multi) {
                            setSelectedRows([]);
                        } else {
                            setActiveRow(null);
                        }
                    }
                }).catch(err => {
                    TnMessage.error("删除失败");
                }).finally(() => {
                    setLoading(false);
                })
            },
            onCancel() {
                Modal.destroyAll();
            },
            closable: true,
        })
    }

    // 添加项目用户
    const onAddFinish = (userIds: number[]) => {
        if (userIds?.length === 0) {
            setAddVisible(false);
            return;
        }

        setAddLoading(true);
        linkProjectUsers(+projectId, userIds).then(res => {
            if (res.status === 200) {
                let newUsers = res.data || [];
                setUsers([...users, ...newUsers]);
                TnMessage.success("添加成功");
                setAddVisible(false);
            }
        }).catch(err => {
            TnMessage.error(err?.data || "添加失败");
        }).finally(() => {
            setAddLoading(false);
        })

    }

    return (
        <div className="user-manager-container">
            <SideTool
                onSelectClick={() => { setMulti(true); }}
                onUnSelectClick={() => { setMulti(false); setSelectedRows([]); }}
                onAddClick={onAddClick}
                onEditClick={() => onEditClick(activeRow)}
                onDeleteClick={() => onDeleteClick(multi, activeRow, selectedRows)}
            />

            <div className="table-area">
                <Table
                    rowKey="id"
                    pagination={false}
                    dataSource={users}
                    className='tn-monix-table'
                    rowClassName={row => classnames({
                        "monix-row-select": activeRow?.id === row.id
                    })}
                    bordered={false}
                    onRow={row => {
                        return {
                            onClick: () => {
                                setActiveRow(row);
                            },
                            onContextMenu: e => {
                                e.stopPropagation();
                                e.preventDefault();
                                setActiveRow(row);
                                setCmOpen(true);
                                setLeft(e.clientX);
                                setTop(e.clientY);
                            },
                            // onMouseEnter: () => setHoverRow(row),
                            // onMouseLeave: () => setHoverRow(null),
                        }
                    }}
                    sticky
                    loading={loading}
                >
                    <Table.Column dataIndex="name" title="名称" width='30%' ellipsis
                        render={(value, row: User) => {
                            return (
                                <Tooltip title={value} placement="bottom" className="multi-cell">
                                    {multi && <div className="multi-icon" onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        let idx = selectedRows?.findIndex(item => item.id === row.id);
                                        if (idx === -1) {
                                            setSelectedRows([...selectedRows, row]);
                                        } else {
                                            selectedRows.splice(idx, 1);
                                            setSelectedRows([...selectedRows]);
                                        }
                                    }}>
                                        {!selectedRows?.some(item => item?.id === row?.id) && <CheckSvg />}
                                        {selectedRows?.some(item => item?.id === row?.id) && <CheckedSvg />}
                                    </div>}
                                    <div className="multi-text">{value}</div>
                                </Tooltip>
                            )
                        }}
                    />
                    <Table.Column dataIndex="positionName" title="职位" width='30%' ellipsis
                        render={(value) => {
                            return (
                                <Tooltip title={value} placement="bottom">
                                    <div className='cell' style={{ color: "#2469EF", textDecorationLine: 'underline', cursor: 'pointer' }}>{value}</div>
                                </Tooltip>
                            )
                        }}
                    />
                    <Table.Column dataIndex="enterprise" title="单位" width='40%' ellipsis
                        render={(value) => {
                            return (
                                <Tooltip title={value} placement="bottom">
                                    <div className='cell'>{value}</div>
                                </Tooltip>
                            )
                        }}
                    />
                </Table>
            </div>

            <ContextMenuFC
                visible={cmOpen}
                top={top}
                left={left}
                width={96}
                wrapperClassname='device-list-right-menu'
                onCancel={() => { setCmOpen(false); setActiveRow(null); }}
            >
                <div className="menu-item" onClick={() => {
                    setDetailVisible(true);
                    setCmOpen(false);
                }}><InfoSvg />详细信息</div>
                <div className="menu-item" onClick={() => onEditClick(activeRow)}><EditSvg />编辑</div>
                <div className="menu-item" onClick={() => onDeleteClick(false, activeRow, [])}><DeleteSvg />删除</div>
            </ContextMenuFC>

            {addVisible && <LinkProjectUserModal
                filterItems={users}
                visible={addVisible}
                setVisible={setAddVisible}
                onFinish={onAddFinish}
                loading={addLoading}
            />}

            {detailVisible && <ProjectUserDetailModal
                userId={activeRow?.id}
                visible={detailVisible}
                setVisible={setDetailVisible}
            />}

            {editVisible && <EditProjectUserModal
                userId={activeRow?.id}
                showModel={editVisible}
                setShowModel={setEditVisible}
                loading={false}
                onSuccess={() => {
                    generateData();
                    setEditVisible(false);
                }}
            />}
        </div>
    )
}